import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { apiRequest } from "../helpers/api";
import { getLoggedInUser, logout } from "../helpers/authHelper";
import { isImpersonating, restoreAdminSession } from "../helpers/impersonation";
import "./ImpersonationBanner.css";

const ADMIN_RETURN_PATH = "/admin/users";

const remainingLabel = (expiresAt, now) => {
  const ms = new Date(expiresAt).getTime() - now;
  if (!Number.isFinite(ms)) return "";
  if (ms <= 0) return "Session expired";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, "0")} left`;
};

// Shown on every page while an admin is signed in as a customer, so it is never mistaken for a normal session.
const ImpersonationBanner = () => {
  const [user, setUser] = useState(() => getLoggedInUser());
  const [active, setActive] = useState(() => isImpersonating());
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    const refresh = () => {
      setUser(getLoggedInUser());
      setActive(isImpersonating());
    };
    // A revoked or expired session has already been swapped back to the admin's session by apiRequest.
    const returnToAdmin = () => window.location.assign(ADMIN_RETURN_PATH);
    window.addEventListener("auth:user-updated", refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener("auth:impersonation-ended", returnToAdmin);
    return () => {
      window.removeEventListener("auth:user-updated", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener("auth:impersonation-ended", returnToAdmin);
    };
  }, []);

  const visible = active && user?.role === "user";
  const customerId = user?.id;
  const leave = useCallback(async () => {
    setLeaving(true);
    try {
      await apiRequest("/api/auth/impersonation/end", { method: "POST" });
    } catch {
      // The session may already be over; returning still works because the admin session is kept locally.
    }
    // apiRequest restores the admin session itself when the session had already expired.
    if (restoreAdminSession() || getLoggedInUser()?.role === "admin") window.location.assign(`${ADMIN_RETURN_PATH}?user=${customerId}&tab=security`);
    else {
      logout();
      window.location.assign("/login");
    }
  }, [customerId]);

  // The page is offset by the banner's real height, which changes when it wraps on small screens.
  useLayoutEffect(() => {
    document.body.classList.toggle("has-impersonation-banner", visible);
    const banner = bannerRef.current;
    if (!visible || !banner) return () => document.body.classList.remove("has-impersonation-banner");
    const update = () => document.body.style.setProperty("--impersonation-banner-height", `${banner.offsetHeight}px`);
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      document.body.classList.remove("has-impersonation-banner");
      document.body.style.removeProperty("--impersonation-banner-height");
    };
  }, [visible]);

  const expiresAt = user?.impersonation?.expiresAt;
  useEffect(() => {
    if (!visible || !expiresAt) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [visible, expiresAt]);

  useEffect(() => {
    if (visible && expiresAt && new Date(expiresAt).getTime() <= now && !leaving) leave();
  }, [visible, expiresAt, now, leaving, leave]);

  if (!visible) return null;
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
  return (
    <div ref={bannerRef} className="impersonation-banner" role="status" aria-live="polite">
      <i className="ti ti-user-shield" aria-hidden="true" />
      <p>
        <strong>Signed in as {name}</strong>
        <span>{user.email !== name ? `${user.email} · ` : ""}Support session by {user.impersonation?.adminName || "an administrator"}. Every change is recorded.</span>
      </p>
      {expiresAt && <span className="impersonation-banner-timer"><i className="ti ti-clock" aria-hidden="true" /> {remainingLabel(expiresAt, now)}</span>}
      <button type="button" onClick={leave} disabled={leaving}>
        <i className="ti ti-arrow-back-up" aria-hidden="true" /> {leaving ? "Returning…" : "Return to admin"}
      </button>
    </div>
  );
};

export default ImpersonationBanner;
